import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { PageHeader, Card, Button, Input, colors } from '../components/SharedComponents';
import { API_BASE } from '../config';

function Profile() {
  const { token } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [originalRoleTitle, setOriginalRoleTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null

  // Business context fields (WS13A)
  const [businessContext, setBusinessContext] = useState({ procurement_priorities: '', business_context: '' });
  const [contextSaving, setContextSaving] = useState({});
  const [contextStatus, setContextStatus] = useState({});

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwChanging, setPwChanging] = useState(false);
  const [pwStatus, setPwStatus] = useState(null); // { type: 'success'|'error', message: string } | null

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    fetchProfile();
    fetchBusinessContext();
  }, [token]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/user/profile`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setFullName(data.full_name || '');
        setEmail(data.email || '');
        setRoleTitle(data.role_title || '');
        setOriginalRoleTitle(data.role_title || '');
      }
    } catch (e) {
      console.error('Failed to fetch profile:', e);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const res = await fetch(`${API_BASE}/api/user/profile`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ role_title: roleTitle.trim() || null })
      });
      if (res.ok) {
        const data = await res.json();
        const saved = data.role_title || '';
        setRoleTitle(saved);
        setOriginalRoleTitle(saved);
        setSaveStatus('success');
      } else {
        setSaveStatus('error');
      }
    } catch (e) {
      console.error('Failed to save profile:', e);
      setSaveStatus('error');
    }
    setSaving(false);
    setTimeout(() => setSaveStatus(null), 4000);
  };

  const fetchBusinessContext = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/company/context`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setBusinessContext({
          procurement_priorities: data.procurement_priorities || '',
          business_context: data.business_context || '',
        });
      }
    } catch (e) {
      console.error('Failed to fetch business context:', e);
    }
  };

  const saveContextField = async (fieldName) => {
    setContextSaving(prev => ({ ...prev, [fieldName]: true }));
    setContextStatus(prev => ({ ...prev, [fieldName]: null }));
    try {
      const res = await fetch(`${API_BASE}/api/company/context`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ [fieldName]: businessContext[fieldName] }),
      });
      if (res.ok) {
        setContextStatus(prev => ({ ...prev, [fieldName]: 'saved' }));
        setTimeout(() => setContextStatus(prev => ({ ...prev, [fieldName]: null })), 3000);
      } else {
        setContextStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      }
    } catch {
      setContextStatus(prev => ({ ...prev, [fieldName]: 'error' }));
    } finally {
      setContextSaving(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleChangePassword = async () => {
    setPwStatus(null);
    if (newPassword.length < 8) {
      setPwStatus({ type: 'error', message: 'New password must be at least 8 characters' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwStatus({ type: 'error', message: 'New passwords do not match' });
      return;
    }
    setPwChanging(true);
    try {
      const res = await fetch(`${API_BASE}/api/profile/change-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwStatus({ type: 'success', message: data.message || 'Password changed successfully' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPwStatus({ type: 'error', message: data.detail || 'Failed to change password' });
      }
    } catch {
      setPwStatus({ type: 'error', message: 'Network error. Please try again' });
    } finally {
      setPwChanging(false);
    }
  };

  const hasChanged = roleTitle !== originalRoleTitle;

  if (loading) {
    return (
      <div style={{ padding: '32px 40px' }}>
        <PageHeader title="Profile" subtitle="Manage your account settings" />
        <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 40px', maxWidth: '680px' }}>
      <PageHeader title="Profile" subtitle="Manage your account settings" />

      {/* Profile info card */}
      <Card padding="28px" style={{ marginBottom: '20px' }}>
        <div style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: 'var(--letter-spacing-wide)',
          marginBottom: '20px',
        }}>
          Account Information
        </div>

        {/* Name — display only */}
        <div style={{ marginBottom: '18px' }}>
          <label style={{
            display: 'block',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: colors.darkText,
            marginBottom: '6px',
          }}>
            Name
          </label>
          <div style={{
            padding: '9px 14px',
            fontSize: 'var(--font-size-base)',
            border: `1px solid ${colors.mediumGray}`,
            borderRadius: '6px',
            backgroundColor: '#F9FAFB',
            color: colors.secondaryText,
            lineHeight: 1.5,
          }}>
            {fullName || 'Not available'}
          </div>
        </div>

        {/* Email — display only */}
        <div style={{ marginBottom: '18px' }}>
          <label style={{
            display: 'block',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: colors.darkText,
            marginBottom: '6px',
          }}>
            Email
          </label>
          <div style={{
            padding: '9px 14px',
            fontSize: 'var(--font-size-base)',
            border: `1px solid ${colors.mediumGray}`,
            borderRadius: '6px',
            backgroundColor: '#F9FAFB',
            color: colors.secondaryText,
            lineHeight: 1.5,
          }}>
            {email || 'Not available'}
          </div>
        </div>

        {/* Role title — editable */}
        <div style={{ marginBottom: '4px' }}>
          <label style={{
            display: 'block',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: colors.darkText,
            marginBottom: '6px',
          }}>
            Describe your role
          </label>
          <Input
            type="text"
            value={roleTitle}
            onChange={(e) => setRoleTitle(e.target.value)}
            placeholder="e.g. Metals Category Manager"
            maxLength={100}
          />
          <div style={{
            fontSize: 'var(--font-size-xs)',
            color: colors.textMuted,
            marginTop: '6px',
          }}>
            Used to personalize your daily procurement briefing
          </div>
        </div>
      </Card>

      {/* Business Context (WS13A) */}
      <Card padding="28px" style={{ marginBottom: '20px' }}>
        <div style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: 'var(--letter-spacing-wide)',
          marginBottom: '8px',
        }}>
          Business Context
        </div>
        <div style={{
          fontSize: 'var(--font-size-sm)',
          color: colors.secondaryText,
          marginBottom: '20px',
          lineHeight: '1.5',
        }}>
          Help the AI understand your business. These notes are included in every AI response to improve relevance.
        </div>
        <div className="context-section">
          {[
            { key: 'procurement_priorities', label: 'Procurement Priorities', placeholder: 'Cost reduction targets, supplier diversification goals, sustainability initiatives...' },
            { key: 'business_context', label: 'Business Context', placeholder: 'Industry dynamics, growth plans, budget constraints, organizational priorities...' },
          ].map(field => (
            <div key={field.key} className="context-field">
              <label className="context-field-label">{field.label}</label>
              <textarea
                className="context-field-textarea"
                value={businessContext[field.key]}
                onChange={e => setBusinessContext(prev => ({ ...prev, [field.key]: e.target.value }))}
                placeholder={field.placeholder}
                rows={3}
              />
              <div className="context-field-actions">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => saveContextField(field.key)}
                  disabled={contextSaving[field.key]}
                >
                  {contextSaving[field.key] ? 'Saving...' : 'Save'}
                </Button>
                {contextStatus[field.key] === 'saved' && (
                  <span className="context-field-status context-field-status--success">Saved</span>
                )}
                {contextStatus[field.key] === 'error' && (
                  <span className="context-field-status context-field-status--error">Error saving</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Change Password */}
      <Card padding="28px" style={{ marginBottom: '20px' }}>
        <div style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: 'var(--letter-spacing-wide)',
          marginBottom: '20px',
        }}>
          Change Password
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label style={{
            display: 'block',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: colors.darkText,
            marginBottom: '6px',
          }}>
            Current Password
          </label>
          <Input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter current password"
          />
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label style={{
            display: 'block',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: colors.darkText,
            marginBottom: '6px',
          }}>
            New Password
          </label>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 8 characters"
          />
        </div>

        <div style={{ marginBottom: '18px' }}>
          <label style={{
            display: 'block',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: colors.darkText,
            marginBottom: '6px',
          }}>
            Confirm New Password
          </label>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter new password"
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            variant="primary"
            onClick={handleChangePassword}
            disabled={pwChanging || !currentPassword || !newPassword || !confirmPassword}
          >
            {pwChanging ? 'Changing...' : 'Change Password'}
          </Button>

          {pwStatus && (
            <span style={{
              fontSize: 'var(--font-size-sm)',
              color: pwStatus.type === 'success' ? colors.success : colors.danger,
              fontWeight: 'var(--font-weight-medium)',
            }}>
              {pwStatus.message}
            </span>
          )}
        </div>
      </Card>

      {/* Notification preferences placeholder */}
      <Card padding="28px" style={{ marginBottom: '24px' }}>
        <div style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: 'var(--letter-spacing-wide)',
          marginBottom: '12px',
        }}>
          Notification Preferences
        </div>
        <div style={{
          fontSize: 'var(--font-size-sm)',
          color: colors.textMuted,
          fontStyle: 'italic',
        }}>
          Coming soon: notification preferences will be configurable here.
        </div>
      </Card>

      {/* Save bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanged || saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>

        {saveStatus === 'success' && (
          <span style={{ fontSize: 'var(--font-size-sm)', color: colors.success, fontWeight: 'var(--font-weight-medium)' }}>
            Changes saved
          </span>
        )}
        {saveStatus === 'error' && (
          <span style={{ fontSize: 'var(--font-size-sm)', color: colors.danger, fontWeight: 'var(--font-weight-medium)' }}>
            Save failed. Please try again
          </span>
        )}
      </div>
    </div>
  );
}

export default Profile;
